/// <amd-module name="Repos/Views/Settings/Repositories/Components/AllRepositories" />
import React = require("react");
import { GitRepositoryPermissions } from "Repos/Common/Permissions";
import { ISettingsViewService, SettingsTabIds } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { RepositoriesTable } from "Repos/Views/Settings/Repositories/Components/RepositoriesTable";
import { RepositoriesZeroData } from "Repos/Views/Settings/Repositories/Components/RepositoriesZeroData";
import { IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { IRepositoriesViewService } from "Repos/Views/Settings/Repositories/Services/RepositoriesViewService";
import { IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tab, TabBar } from "VSSUI/Tabs";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { IFilter } from "VSSUI/Utilities/Filter";
import { RepositoryPermissions } from "./RepositoryPermissions";
import { RepositoryPolicies } from "./RepositoryPolicies";

interface IAllRepositoriesProps {
    renderCreatePanel(): void;
}

export class AllRepositories extends VssComponent<IAllRepositoriesProps> {
    private commandBarItems: IHeaderCommandBarItem[];
    private data: IReadonlyObservableArray<IRepository | IReadonlyObservableValue<IRepository | undefined>>;
    private filter: IFilter;
    private selectedTabId: IReadonlyObservableValue<string | undefined>;
    private svc: IRepositoriesViewService;

    public constructor(props: IAllRepositoriesProps, context: IVssComponentContext) {
        super(props, context);

        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.selectedTabId = this.svc.getSelectedTab();

        this.filter = this.svc.getFilter();
        this.filter.reset();
        this.data = this.svc.getFilteredRepositories();
        this.setCommandBarItems();
    }

    public render = (): JSX.Element => {
        const settingsService = this.context.pageContext.getService<ISettingsViewService>("SettingsViewService");

        return (
            <>
                <Header
                    contentClassName="flex-wrap"
                    titleSize={TitleSize.Large}
                    title={Resources.AllRepositories}
                    commandBarItems={this.commandBarItems}
                />
                <Observer data={this.data} selectedTabId={this.selectedTabId}>
                    {(props: { data: Array<IRepository | IReadonlyObservableValue<IRepository | undefined>>; selectedTabId: string | undefined }) => {
                        return (
                            <>
                                <TabBar
                                    className="flex-wrap"
                                    selectedTabId={this.selectedTabId.value || SettingsTabIds.repositories}
                                    onSelectedTabChanged={this.onSelectedTabChanged}
                                    renderAdditionalContent={this.renderFilter}
                                >
                                    <Tab
                                        id={SettingsTabIds.repositories}
                                        key={SettingsTabIds.repositories}
                                        name={Resources.RepositoriesTab}
                                        url={settingsService.getUrl(undefined, undefined, SettingsTabIds.repositories)}
                                    />
                                    <Tab
                                        id={SettingsTabIds.settings}
                                        key={SettingsTabIds.settings}
                                        name={Resources.SettingsTab}
                                        url={settingsService.getUrl(undefined, undefined, SettingsTabIds.settings)}
                                    />
                                    <Tab
                                        id={SettingsTabIds.policies}
                                        key={SettingsTabIds.policies}
                                        name={Resources.PoliciesTab}
                                        url={settingsService.getUrl(undefined, undefined, SettingsTabIds.policies)}
                                    />
                                    <Tab
                                        id={SettingsTabIds.permissions}
                                        key={SettingsTabIds.permissions}
                                        name={Resources.SecurityTab}
                                        url={settingsService.getUrl(undefined, undefined, SettingsTabIds.permissions)}
                                    />
                                </TabBar>

                                <div className="page-content page-content-top">
                                    {props.selectedTabId == SettingsTabIds.policies && <RepositoryPolicies />}
                                    {props.selectedTabId == SettingsTabIds.permissions && <RepositoryPermissions />}
                                    {props.selectedTabId == SettingsTabIds.settings && (
                                        <WrappedComponent
                                            loadingComponent={renderLoading}
                                            dependencies={["ms.vss-code-web.settings-options"]}
                                            wrappedProps={{ projectId: this.svc.getProject().id }}
                                            wrappedType="ms.vss-code-web.settings-options-component"
                                        />
                                    )}
                                    {this.isDefaultTabShown() && props.data.length === 0 && (
                                        <RepositoriesZeroData hasCreateRepoPermissions={this.svc.checkPermissions(GitRepositoryPermissions.CreateRepository)} renderCreatePanel={this.props.renderCreatePanel} />
                                    )}
                                    {this.isDefaultTabShown() && props.data.length > 0 && <RepositoriesTable />}
                                </div>
                            </>
                        );
                    }}
                </Observer>
            </>
        );
    };

    private isDefaultTabShown = (): boolean => {
        const result =
            this.selectedTabId.value !== SettingsTabIds.policies &&
            this.selectedTabId.value !== SettingsTabIds.permissions &&
            this.selectedTabId.value !== SettingsTabIds.settings;

        if (result) {
            this.svc.setSelectedTab(SettingsTabIds.repositories, true);
        }

        return result;
    };

    private onSelectedTabChanged = (newTabId: string) => {
        this.svc.setSelectedTab(newTabId);
    };

    private setCommandBarItems = () => {
        if (this.svc.checkPermissions(GitRepositoryPermissions.CreateRepository)) {
            this.commandBarItems = [
                {
                    ariaLabel: Resources.CreateLabel,
                    iconProps: { iconName: "Add" },
                    id: "add",
                    isPrimary: true,
                    onActivate: () => {
                        this.props.renderCreatePanel();
                    },
                    text: Resources.Create
                }
            ];
        }
    };

    private renderFilter = () => {
        return (
            <Observer selectedTabId={this.selectedTabId}>
                {(props: { selectedTabId: string | undefined }) => {
                    return (
                        props.selectedTabId === SettingsTabIds.repositories && (
                            <InlineKeywordFilterBarItem filter={this.filter} filterItemKey="name" />
                        )
                    );
                }}
            </Observer>
        );
    };
}

function renderLoading() {
    return <Spinner size={SpinnerSize.large} />;
}
